import React, { useRef, useState } from "react";
import styled from "styled-components";
import { FontIcon } from "@fluentui/react";
import { CompanyOnboardingStrings } from "@/screen/Companies/companyOnboarding.strings";

// Formatos admitidos para cada cara del DNI
const AcceptedExtensions = [".pdf", ".jpg", ".jpeg", ".png"];

export function isAcceptedIdDocumentFile(file: File): boolean
{
    const fileName = file.name.toLowerCase();
    return AcceptedExtensions.some((extension) => fileName.endsWith(extension));
}

export function formatFileSize(bytes: number): string
{
    if(bytes < 1024 * 1024)
    {
        return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}

interface IdDocumentSideUploadProps
{
    label: string;
    file: File | null;
    onFileChange: (file: File) => void;
}

// Zona de subida de una cara del DNI: selección por botón o arrastrando el archivo
const IdDocumentSideUpload: React.FC<IdDocumentSideUploadProps> = ({ label, file, onFileChange }) =>
{
    const inputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const selectFile = (selected: File | undefined): void =>
    {
        if(!selected)
        {
            return;
        }
        if(!isAcceptedIdDocumentFile(selected))
        {
            setError(CompanyOnboardingStrings.idDocumentInvalidFormat);
            return;
        }
        setError(null);
        onFileChange(selected);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void =>
    {
        selectFile(e.target.files?.[0]);
        // Permite volver a elegir el mismo archivo tras reemplazarlo
        e.target.value = "";
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>): void =>
    {
        e.preventDefault();
        setIsDragging(false);
        selectFile(e.dataTransfer.files?.[0]);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void =>
    {
        e.preventDefault();
        setIsDragging(true);
    };

    const hasFile = file !== null;

    return(
        <DropZone className={`${hasFile ? "loaded" : ""} ${isDragging ? "dragging" : ""}`} onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={() => setIsDragging(false)}>
            <FontIcon iconName={hasFile ? "ContactCard" : "Upload"} className="drop-icon" />
            <b>{label}</b>
            <span className="hint">
                {hasFile ? `${file.name} · ${formatFileSize(file.size)}` : CompanyOnboardingStrings.idDocumentDropHint}
            </span>
            {error && <span className="error" role="alert">{error}</span>}
            <OutlineButton type="button" onClick={() => inputRef.current?.click()}>
                {hasFile ? CompanyOnboardingStrings.idDocumentReplace : CompanyOnboardingStrings.idDocumentSelect}
            </OutlineButton>
            <input ref={inputRef} type="file" accept={AcceptedExtensions.join(",")} onChange={handleInputChange} hidden aria-label={label} />
        </DropZone>
    );
};

export default IdDocumentSideUpload;

const DropZone = styled.div`
    border: 1.5px dashed #C3CDD1;
    border-radius: 18px;
    background: #ffffff;
    padding: 28px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 10px;
    min-width: 0;

    b
    {
        font-size: 17px;
        color: #2a4143;
    }

    .hint
    {
        font-size: 13px;
        color: #5f7172;
        overflow-wrap: anywhere;
    }

    .error
    {
        font-size: 13px;
        color: #a4262c;
    }

    .drop-icon
    {
        font-size: 36px;
        color: ${(props) => props.theme?.palette?.themePrimary || "#CC214F"};
    }

    &.dragging
    {
        border-color: ${(props) => props.theme?.palette?.themePrimary || "#CC214F"};
        background: #FBEAEF;
    }

    &.loaded
    {
        border-style: solid;
        border-color: #9FD1AE;
        background: #F4FBF6;

        .drop-icon
        {
            color: #237A3E;
        }
    }
`;

const OutlineButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 42px;
    padding: 0 28px;
    border: 2px solid ${(props) => props.theme?.palette?.themePrimary || "#CC214F"};
    border-radius: 999px;
    background: #ffffff;
    color: ${(props) => props.theme?.palette?.themePrimary || "#CC214F"};
    font: inherit;
    font-weight: 600;
    font-size: 16px;
    cursor: pointer;
    white-space: nowrap;

    &:hover
    {
        background: #FBEAEF;
    }
`;
